import BusinessHeaderAutoCompleteList from '@/components/auto-complete-form/business-header-autocomplete-list';
import Icon, { IconType } from '@/components/icons';
import { useBusinessLayout } from '@/contexts/LayoutContext';
import { DEFAULT_LANG, LOCALE_LINKS, useLocale } from '@/helpers/LangHelper';
import useOnClickOutside from '@/hook/useOutsideClick';
import { Category } from '@/types/Category';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './business-access-primary-actions.module.scss';
import classNames from 'classnames';
import { BusinessSignUpButton } from '../business-sign-up-button/business-sign-up-button';
import DropdownExplore from './dropdown-explore/dropdown-explore';

interface Props {
    isMobile?: boolean;
    isMobileMenuActive?: boolean;
    onToggleMenu?: () => void;
}

export const BusinessAccessPrimaryActions: FC<Props> = (props) => {
    const { isMobile = false, isMobileMenuActive = false, onToggleMenu } = props;
    const { categories: categoriesProps } = useBusinessLayout();

    const { locale } = useLocale();
    const [isShowDrawerContent, setShowMenu] = useState<boolean>(false);
    const [isShowExplore, setShowExplore] = useState<boolean>(false);
    const [isShowFormSearch, setShowFormSearch] = useState<boolean>(false);

    const [categories, setMappedCategories] = useState<Category[]>([]);
    const navRef = useRef(null);

    const showDrawerContent = useMemo(() => {
        if (isMobile) {
            return isMobileMenuActive;
        }

        return isShowDrawerContent;
    }, [isMobile, isMobileMenuActive]);

    useEffect(() => {
        getCategories(categoriesProps);
    }, [categoriesProps]);

    useEffect(() => {
        const body = document.body;
        if (!body) {
            return;
        }

        if (showDrawerContent) {
            body.classList.add('lock-scroll');
        } else {
            body.classList.remove('lock-scroll');
        }
    }, [showDrawerContent]);

    const categoryOrder: string[] = ['News', 'Success Stories', 'Marketing', 'Operations', 'Finance'];
    const getCategories = (categories: Category[]) => {
        if (categories && categories.length > 0) {
            categories.sort((a, b) => {
                const indexOfA = categoryOrder.indexOf(a.name);
                const indexOfB = categoryOrder.indexOf(b.name);

                if (indexOfA !== -1 && indexOfA !== indexOfB) {
                    return indexOfA - indexOfB;
                }
                return a.name.localeCompare(b.name);
            });

            const indx = categories.map((c) => c.name).indexOf(categoryOrder[0]);
            const priorityCategory = categories.slice(indx);
            const normalCategory = categories.slice(0, indx);

            setMappedCategories([...priorityCategory, ...normalCategory]);
        }
    };

    const getGuides = () => {
        return categories
            .filter((category: Category) => {
                if (category.isSeries) {
                    return category;
                }
            })
            .map((category) => ({ name: category.name, guideUrl: category.categoryUrl ?? '' }));
    };

    const sortCategoriesForDropdown = (categories: Category[]) => {
        if (categories && categories.length > 0) {
            let categoriesMap: any = [];
            for (let i = 0; i < categoryOrder.length; i++) {
                const categoryFilter = categories.find((category: Category) => category.name === categoryOrder[i]);
                categoriesMap.push(categoryFilter);
            }
            return categoriesMap;
        }
    };

    useOnClickOutside(navRef, () => {
        setShowExplore(false);
    });

    const toggleShowHideExplore = () => {
        if (!isShowExplore) {
            setShowFormSearch(false);
        }
        setShowExplore(!isShowExplore);
    };

    const handleToggleFormSearch = () => {
        setShowFormSearch(!isShowFormSearch);
    };

    const signupButton = () => (
        <div className={styles.cta}>
            <BusinessSignUpButton signupLink={(LOCALE_LINKS[locale] || LOCALE_LINKS[DEFAULT_LANG])!.signup} isMobile={isMobile} />
        </div>
    );

    const renderDropDownExplore = useCallback(() => {
        if (isMobile) {
            return null;
        }

        return (
            <>
                <div className={classNames(styles.buttonExplore, { [styles.buttonActive]: isShowExplore })} onClick={() => toggleShowHideExplore()}>
                    Explore
                    <div className={styles.iconDown}>
                        <Icon type={IconType.Down} />
                    </div>
                </div>
                <DropdownExplore isToggle={isShowExplore} categories={sortCategoriesForDropdown(categories)} guides={getGuides()} isMobile={isMobile} />
            </>
        );
    }, [isMobile, isShowExplore]);

    const renderButtonToggleDrawer = () => {
        if (isMobile) {
            return null;
        }

        return (
            <div className={styles.menuRight}>
                <BusinessHeaderAutoCompleteList
                    isShown={isShowFormSearch}
                    onClickButtonSearch={() => handleToggleFormSearch()}
                    onFocus={() => setShowFormSearch(true)}
                    onSearch={() => {
                        setShowExplore(false);
                    }}
                    onClose={() => setShowFormSearch(false)}
                />
                {signupButton()}
            </div>
        );
    };
    const renderDrawerContent = () => {
        if (!showDrawerContent) {
            return null;
        }

        return (
            <div className={classNames(styles.drawerMenu, { [styles.mobile]: isMobile })}>
                <div className={styles.drawerMenuButton}>
                    <BusinessHeaderAutoCompleteList
                        isShown={isShowFormSearch}
                        onClickButtonSearch={() => setShowFormSearch(true)}
                        onFocus={() => setShowFormSearch(true)}
                        onClose={() => setShowFormSearch(false)}
                    />
                    {!isMobile && !isShowFormSearch && <div className={styles.accountGroup}>{signupButton()}</div>}
                </div>
                {!isShowFormSearch && <DropdownExplore isToggle categories={sortCategoriesForDropdown(categories)} isMobile={isMobile} />}
            </div>
        );
    };

    return (
        <>
            {isMobile ? <div className={classNames(styles.background, { [styles.show]: showDrawerContent })}></div> : null}
            <div className={classNames(styles.container, { [styles.mobile]: isMobile })} ref={navRef}>
                <div className={classNames(styles.articleHeaderContainer, { [styles.toggleMenu]: showDrawerContent })}>
                    <div className={styles.logoWrapper}>{renderDropDownExplore()}</div>
                    {renderButtonToggleDrawer()}
                </div>
                {renderDrawerContent()}

                {isMobile && signupButton()}
            </div>
        </>
    );
};
